import { computed, Injectable, signal, Signal } from "@angular/core";
import { ChartCardConfig } from "../models/chart-config.model";
import { UlidService } from "../services/ulid.service";
import {CardConfig, CardError, CardType} from "../models/card-model";
import { sanitizeImageCardRefs } from "../utils/card-image-ref.util";

@Injectable({
  providedIn: "root",
})
export class CardStateService {
  private cardsSignal = signal<Map<string, CardConfig>>(
    new Map<string, CardConfig>()
  );

  public readonly allCards = computed(() =>
    Array.from(this.cardsSignal().values())
  );

  constructor(private ulidService: UlidService) {}

  #sanitizeImageCardConfig<T extends CardConfig>(config: T): T {
    if (config.cardType !== CardType.Image) {
      return config;
    }
    return sanitizeImageCardRefs(config) as T;
  }

  addCard(id: string, config: CardConfig) {
    const updatedCards = new Map(this.cardsSignal());
    updatedCards.set(id, this.#sanitizeImageCardConfig(config));
    this.cardsSignal.set(updatedCards);
  }

  deleteCard(id: string) {
    const updatedCards = new Map(this.cardsSignal());
    updatedCards.delete(id);
    this.cardsSignal.set(updatedCards);
  }

  /**
   * Moves a card entry to a new id (e.g. client draft id → server `cardId` after createCard).
   * Preserves local edits; updates `cardId` on the stored config.
   */
  replaceCardId(oldId: string, newId: string): void {
    const cards = this.cardsSignal();
    const current = cards.get(oldId);
    if (!current) {
      return;
    }
    const next = { ...current, cardId: newId } as CardConfig;
    const updatedCards = new Map(cards);
    updatedCards.delete(oldId);
    updatedCards.set(newId, next);
    this.cardsSignal.set(updatedCards);
  }

  updateCard(id: string, updates: Partial<CardConfig>) {
    const cards = this.cardsSignal();
    const current = cards.get(id);

    if (current) {
      // Only check timestamps for content updates (html/markdown), not chart data
      const hasContentUpdate = updates.content || (updates as any).html || (updates as any).markdown;
      const isChartDataUpdate = updates.cardType === 'chart' && (updates as any).data;
      
      // Skip timestamp check for chart data updates (handled by DataService) or content updates
      if (updates.requestId && current.requestId && !hasContentUpdate && !isChartDataUpdate) {
        // Use UlidService to compare timestamps only for non-chart, non-content data updates
        const comparison = this.ulidService.compare(
          updates.requestId,
          current.requestId
        );

        if (comparison <= 0) {
          // If new data is older or same age
          console.warn(
            `[CardState] Data update for card ${id} ignored - older timestamp`
          );
          return;
        }
      }

      // Apply the update - latest config always wins
      const updated = this.#sanitizeImageCardConfig({
        ...current,
        ...updates,
      } as CardConfig);
      const updatedCards = new Map(cards);
      updatedCards.set(id, updated as CardConfig);
      this.cardsSignal.set(updatedCards);
    } else {
      // If card doesn't exist, create it with the updates
      const updatedCards = new Map(cards);
      updatedCards.set(
        id,
        this.#sanitizeImageCardConfig(updates as CardConfig)
      );
      this.cardsSignal.set(updatedCards);
    }
  }

  getCard(id: string): CardConfig | undefined {
    return this.cardsSignal().get(id);
  }

  getAllCards(): Map<string, CardConfig> {
    return this.cardsSignal();
  }

  getCardSignal(id: string): Signal<CardConfig | undefined> {
    return computed(() => this.cardsSignal().get(id));
  }

  clearAllCards(): void {
    this.cardsSignal.set(new Map<string, CardConfig>());
  }

  /**
   * Sets an error state for a specific card
   * @param id Card identifier
   * @param error The error to set
   */
  setCardError(id: string, error: CardError): void {
    const cards = this.cardsSignal();
    const current = cards.get(id);

    if (current) {
      const updated = { ...current, error };
      const updatedCards = new Map(cards);
      updatedCards.set(id, updated as CardConfig);
      this.cardsSignal.set(updatedCards);
      console.error(`[CardState] Error set for card ${id}:`, error);
    } else {
      // Create a minimal card entry with the error
      const updatedCards = new Map(cards);
      updatedCards.set(id, { cardId: id, error } as CardConfig);
      this.cardsSignal.set(updatedCards);
      console.error(`[CardState] Error set for non-existent card ${id}:`, error);
    }
  }

  /**
   * Clears the error state for a specific card
   * @param id Card identifier
   */
  clearCardError(id: string): void {
    const cards = this.cardsSignal();
    const current = cards.get(id);

    if (current && current.error) {
      const { error, ...rest } = current;
      const updatedCards = new Map(cards);
      updatedCards.set(id, rest as CardConfig);
      this.cardsSignal.set(updatedCards);
      console.log(`[CardState] Error cleared for card ${id}`);
    }
  }

  /**
   * Gets the error state for a specific card
   * @param id Card identifier
   * @returns The card error or undefined
   */
  getCardError(id: string): CardError | undefined {
    return this.cardsSignal().get(id)?.error;
  }
}
